import { CalibrationType } from "@/shared/enums/CalibrationType";
import { SessionType } from "@/shared/enums/SessionType";
import { AUGGIEAVAILABLE } from "@/main";
import { CalibrationSessionDto } from "@/shared/models/CalibrationSessionDto";
import CameraViewerConfiguration from "@/shared/models/CameraViewerConfiguration";
import { PackageDto } from "@/shared/models/PackageDto";
import { VehicleDto } from "@/shared/models/VehicleDto";
import { MarkerType } from "@/shared/enums/MarkerType";
import BatteryInfoDto from "@/shared/models/BatteryInfoDto";
import TemperatureInfoDto from "@/shared/models/TemperatureInfoDto";
import CellularSignalDto from "@/shared/models/CellularSignalDto";
import CellularInfoDto from "@/shared/models/CellularInfoDto";

export class GlobalData {

    public MainApp: any = null!;
    public MainHome: any = null!;
    public MainHeader: any = null!;
    public DeviceSerialNumber: string = "";
    public Environment: string = "PRD";
    public PackageUpgrade: PackageDto | null = null;

    public BatteryPackageVersion: string = "2025.044.1"; // first package version that supports BatteryInfo
    public TemperaturePackageVersion: string = "2025.220.1"; // first package version that supports TemperatureInfo
    public DualGyroPackageVersion: string = "2026.056.1"; // first package version that supports dual (internal/external) gyro
    public CameraWindowVisibilityVersion: string = "2026.022.1"; // first package version that supports HideCameraWindow/ShowCameraWindow
    public CellularPackageVersion: string = "2026.111.1"; // first package version that supports Auggie.Cellular bindings
    public CenterlinePackageVersion: string = "2026.196.1"; // first package version that supports SetCenterLineActive/CenterLineReached

    public GetWebAPIUrl(): string {
        let url: string;
        switch (this.Environment) {
            case "DEV":
                url = import.meta.env.VITE_WEBAPI_DEV!;
                break;
            case "QAT":
                url = import.meta.env.VITE_WEBAPI_QAT!;
                break;
            case "UAT":
                url = import.meta.env.VITE_WEBAPI_UAT!;
                break;
            case "STG":
                url = import.meta.env.VITE_WEBAPI_STG!;
                break;
            default:
                url = import.meta.env.VITE_WEBAPI_PRD!;
                break;
        }
        if (!url.endsWith("/"))
            url += "/";
        return url;
    }
    public GetCompanionUrl(): string {
        let url: string;
        switch (this.Environment) {
            case "DEV":
                url = import.meta.env.VITE_COMPANION_DEV!;
                break;
            case "QAT":
                url = import.meta.env.VITE_COMPANION_QAT!;
                break;
            case "UAT":
                url = import.meta.env.VITE_COMPANION_UAT!;
                break;
            case "STG":
                url = import.meta.env.VITE_COMPANION_STG!;
                break;
            default:
                url = import.meta.env.VITE_COMPANION_PRD!;
                break;
        }
        if (!url.endsWith("/"))
            url += "/";
        return url;
    }
    public GetSignalRFunctionUrl(): string {
        switch (this.Environment) {
            case "DEV":
                return import.meta.env.VITE_SignalRFunction_DEV!;
            case "QAT":
                return import.meta.env.VITE_SignalRFunction_QAT!;
            case "UAT":
                return import.meta.env.VITE_SignalRFunction_UAT!;
            case "STG":
                return import.meta.env.VITE_SignalRFunction_STG!;
            default:
                return import.meta.env.VITE_SignalRFunction_PRD!;
        }
    }
    public UseFakeCameraImage(): boolean {
        if (AUGGIEAVAILABLE) {
            return Auggie.System.PredefinedCameraImage();
        } else {
            return import.meta.env.VITE_FAKECAMERAIMAGE === "1";
        }
    }
    public IgnoreCameraCalibration(): boolean {
        if (AUGGIEAVAILABLE) {
            return Auggie.System.IgnoreCameraCalibration();
        } else {
            console.log(`VITE_IGNORECAMERACALIBRATION = ${import.meta.env.VITE_IGNORECAMERACALIBRATION}`);
            return import.meta.env.VITE_IGNORECAMERACALIBRATION === "1";
        }
    }
    public get DetailLog(): boolean {
        if (this.DetailLogCache != null) return this.DetailLogCache;
        if (!AUGGIEAVAILABLE || !Global.IsVersionAtLeast("2023.019.12")) {
            this.DetailLogCache = false;
            return false;
        }
        this.DetailLogCache = Auggie.System.GetDetailLog();
        return this.DetailLogCache || false;
    }
    public set DetailLog(value: boolean) {
        if (!value) return; // we can only turn it on
        if (this.DetailLogCache != null && this.DetailLogCache) return; // already on
        if (!AUGGIEAVAILABLE || !Global.IsVersionAtLeast("2023.019.12")) return;
        Auggie.System.SetDetailLog(true);
        this.DetailLogCache = true;
    }
    public get UseGyroYPRInd(): boolean {
        if (this.UseGyroYPRIndCache != null) return this.UseGyroYPRIndCache;
        if (!AUGGIEAVAILABLE || !Global.IsVersionAtLeast(this.DualGyroPackageVersion) || !Auggie.Gyro.HasDualGyro()) {
            this.UseGyroYPRIndCache = false;
        } else {
            this.UseGyroYPRIndCache = Auggie.Gyro.GetGyroActive();
        }
        return this.UseGyroYPRIndCache ?? false;
    }
    public set UseGyroYPRInd(value: boolean) {
        if (!AUGGIEAVAILABLE || !Global.IsVersionAtLeast(this.DualGyroPackageVersion)) return;
        Auggie.Gyro.SetGyroActive(value);
        this.UseGyroYPRIndCache = Auggie.Gyro.GetGyroActive();
        this.DebugGyroYPRInd = Auggie.Gyro.GetGyroDebug();
    }
    private DebugGyroYPRInd: boolean = false;

    public get DebugGyroYPR(): boolean {
        return this.DebugGyroYPRInd;
    }

    public get HasDualGyro(): boolean {
        if (this.HasDualGyroCache != null) return this.HasDualGyroCache;
        if (!AUGGIEAVAILABLE || !Global.IsVersionAtLeast(this.DualGyroPackageVersion)) {
            this.HasDualGyroCache = false;
            return false;
        }
        this.HasDualGyroCache = Auggie.Gyro.HasDualGyro();
        return this.HasDualGyroCache;
    }
    private HasDualGyroCache: boolean|null = null;

    public GyroMeasurementCaptured: boolean = false;


    /**
     * Check whether the installed device client has at least the specified version.
     */
    public IsVersionAtLeast(reqVersion: string): boolean {
        if (!AUGGIEAVAILABLE) return true;
        const deviceVersion = Auggie.System.Version;

        const devParts = deviceVersion.split(".");
        if (devParts.length !== 3)
            return false;
        const deviceVersionMajor = Number(devParts[0]);
        const deviceVersionMinor = Number(devParts[1]);
        const deviceVersionBuild = Number(devParts[2]);

        const reqParts = reqVersion.split(".");
        if (reqParts.length !== 3)
            return false;
        const reqMajor = Number(reqParts[0]);
        const reqMinor = Number(reqParts[1]);
        const reqBuild = Number(reqParts[2]);

        if (reqMajor > deviceVersionMajor) return false;
        if (reqMajor < deviceVersionMajor) return true;
        if (reqMinor > deviceVersionMinor) return false;
        if (reqMinor < deviceVersionMinor) return true;
        if (reqBuild > deviceVersionBuild) return false;
        if (reqBuild < deviceVersionBuild) return true;
        return true;
    }
    public IsVersionBeyondLastSupported(): boolean {
        if (!AUGGIEAVAILABLE) return false;
        const deviceVersion = Auggie.System.Version;

        // The last supported device client version is set by the build pipeline (is overridden in .env file).
        // VITE_LASTSUPPORTEDDEVICEVERSION is set to the current build number in the build pipeline.
        const lastVersion = import.meta.env.VITE_LASTSUPPORTEDDEVICEVERSION || "";
        if (!lastVersion) return false;

        const devParts = deviceVersion.split(".");
        if (devParts.length !== 3)
            return true;
        const deviceVersionMajor = Number(devParts[0]);
        const deviceVersionMinor = Number(devParts[1]);
        const deviceVersionBuild = Number(devParts[2]);

        const lastParts = lastVersion.split(".");
        if (lastParts.length !== 3)
            return true;
        const lastMajor = Number(lastParts[0]);
        const lastMinor = Number(lastParts[1]);
        const lastBuild = Number(lastParts[2]);

        if (lastMajor > deviceVersionMajor) return false;
        if (lastMajor < deviceVersionMajor) return true;
        if (lastMinor > deviceVersionMinor) return false;
        if (lastMinor < deviceVersionMinor) return true;
        if (lastBuild > deviceVersionBuild) return false;
        if (lastBuild < deviceVersionBuild) return true;
        return false;
    }

    public IsPrototype(): boolean {
        if (!AUGGIEAVAILABLE || !Global.IsVersionAtLeast("2023.186.1")) return false;
        return Auggie.Device.Prototype();
    }

    public IsDemo(): boolean {
        if (!AUGGIEAVAILABLE || !Global.IsVersionAtLeast("2023.195.1")) return false;
        return Auggie.Device.Demo();
    }

    public IsAir(): boolean {
        if (!AUGGIEAVAILABLE || !Global.IsVersionAtLeast("2026.014.1")) return false;
        return Auggie.Device.DeviceType() === "Air";
    }

    public IsCenterlineSupported(): boolean {
        return Global.IsVersionAtLeast(this.CenterlinePackageVersion);
    }

    public DeviceGuid: string = "";
    public DeviceName: string = "";
    public AllowAssistedCalibration: boolean = false;
    public AllowAssistedTargetDisplay: boolean = false;
    public AllowSelfCalibration: boolean = false;
    public DetailLogCache: boolean|null = null;
    public VerifiedVehiclesOnlyInd: boolean = true;
    public CenterlineEnabledInd: boolean = false;
    public CenterlineCompleted: boolean = false;
    public UseGyroYPRIndCache: boolean|null = null;
    public BatteryInfo: BatteryInfoDto|null = null;
    public TemperatureInfo: TemperatureInfoDto|null = null;
    public CellularSignal: CellularSignalDto|null = null;
    public CellularInfo: CellularInfoDto|null = null;
    public AmbientLightAlignment: number|null = null;
    public AmbientLightTarget: number|null = null;

    // Session information

    public CalibrationSession: CalibrationSessionDto|null = null;
    public CameraViewerConfiguration: CameraViewerConfiguration|null = null;
    public Make: string = "";
    public Model: string = "";
    public Year: number = 0;
    public Calibration: CalibrationType = CalibrationType.All;
    public LegLengthA: number = 0;
    public LegLengthB: number = 0;
    public LegLengthC: number = 0;
    public Marker: MarkerType = MarkerType.Standard;
    public WheelArchHeightMin: number|null = null;
    public WheelArchHeightMax: number|null = null;
    public TechnicianName: string = "";
    public VehicleId: number|null = null;
    public SessionType: SessionType|null = null;
    public IsTechControlled: boolean = false;
    public LightIntensity: number = 50;
    public OriginalWindshieldAngle: number|null = null;
    public DemoCameraImage: string = "";
    public DemoVehicle: VehicleDto|null = null;

    public clear(): void {
        this.CalibrationSession = null;
        this.CameraViewerConfiguration = null;
        this.Make = "";
        this.Model = "";
        this.Year = 0;
        this.Calibration = CalibrationType.All;
        this.LegLengthA = 0;
        this.LegLengthB = 0;
        this.LegLengthC = 0;
        this.Marker = MarkerType.Standard;
        this.WheelArchHeightMin = null;
        this.WheelArchHeightMax = null;
        this.TechnicianName = "";
        this.VehicleId = null;
        this.SessionType = null;
        this.IsTechControlled = false;
        this.OriginalWindshieldAngle = null;
        this.DemoCameraImage = "";
        this.AmbientLightAlignment = null;
        this.AmbientLightTarget = null;
        this.GyroMeasurementCaptured = false;
    }
}
export const Global = new GlobalData();